import { NavLink, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import {
  LogOut,
  LayoutDashboard,
  Megaphone,
  UserSquare,
  Users,
  Wallet,
} from 'lucide-react';

import { Logo } from '../components/atoms/logo/logo';
import { ThemeToggle } from '../components/atoms/theme-toggle/theme-toggle';
import { useAuth } from '../hooks/use-auth';

const Shell = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bgPage};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  background: ${({ theme }) => theme.colors.bgSurface};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;
  font-size: 14px;
  text-align: left;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &.active {
    background: ${({ theme }) => theme.colors.bgMuted};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Content = styled.section`
  display: flex;
  flex-direction: column;
`;

const Topbar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 32px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSurface};
`;

const UserBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textInverse};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
`;

const UserMeta = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;

  strong {
    font-weight: 600;
    font-size: 14px;
  }
  span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSurface};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
  font-weight: 500;
  transition: all ${({ theme }) => theme.transitions.base};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Main = styled.main`
  flex: 1;
  padding: 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 24px 20px;
  }
`;

const initials = (name?: string) =>
  name
    ?.split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') ?? '?';

export const AuthenticatedLayout = () => {
  const { user, logout, hasPermission } = useAuth();

  return (
    <Shell>
      <Sidebar>
        <Logo size={22} />
        <Nav aria-label="Navegação principal">
          <NavItem to="/" end>
            <LayoutDashboard size={16} />
            Início
          </NavItem>
          {hasPermission('CLIENTE_READ') && (
            <NavItem to="/clientes">
              <UserSquare size={16} />
              Clientes
            </NavItem>
          )}
          {hasPermission('SAQUE_READ') && (
            <NavItem to="/saques">
              <Wallet size={16} />
              Saques
            </NavItem>
          )}
          {hasPermission('USER_READ') && (
            <NavItem to="/admin/usuarios">
              <Users size={16} />
              Usuários
            </NavItem>
          )}
          {hasPermission('AVISO_READ') && (
            <NavItem to="/avisos">
              <Megaphone size={16} />
              Avisos
            </NavItem>
          )}
        </Nav>
      </Sidebar>

      <Content>
        <Topbar>
          <UserBlock>
            <Avatar aria-hidden="true">{initials(user?.name)}</Avatar>
            <UserMeta>
              <strong>{user?.name ?? 'Usuário'}</strong>
              <span>{user?.email ?? ''}</span>
            </UserMeta>
          </UserBlock>
          <Actions>
            <ThemeToggle />
            <IconButton type="button" onClick={() => logout()}>
              <LogOut size={16} />
              Sair
            </IconButton>
          </Actions>
        </Topbar>
        <Main>
          <Outlet />
        </Main>
      </Content>
    </Shell>
  );
};
